import React from 'react';
import styled from '@emotion/native';
import { ImageSourcePropType } from 'react-native';
import { H2, BodyText } from '@/shared/components/StyledText';
import {
  accent,
  green,
  sand,
  welcomeMutedOnGreen,
} from '@/shared/styles/colors';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import {
  FONT_STYLES,
  WELCOME_HOW_EMME_WORKS_SECTION_PADDING_Y,
  WELCOME_SECTION_BODY_COPY,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
} from '@/shared/styles/constants';
import { IS_WEB } from '@/shared/constants/utility';

/** Slightly wider than global welcome max so three phone columns can breathe */
const HOW_EMME_WORKS_WIDE_INNER_MAX_WIDTH = 1200;

const STEP_COLUMN_MAX_WIDTH_MOBILE = 340;

/** Device-frame PNG exported @1.5× (656×1323 px); aspect ratio matches ~437×882 layout */
const STEP_DEVICE_FRAME_ASPECT = 656 / 1323;
const STEP_SCREEN_INSET = {
  top: '1.7%',
  left: '5.05%',
  right: '4.85%',
  bottom: '1.7%',
} as const;

interface StepsLayoutProps {
  isWideSteps: boolean;
}

interface StepCopy {
  step: number;
  title: string;
  description: string;
  imageSource: ImageSourcePropType;
}

const STEPS: StepCopy[] = [
  {
    step: 1,
    title: 'Find The Right Stylist',
    description:
      'Browse vetted professionals by expertise, aesthetic, location, and availability.',
    imageSource: require('@/assets/images/Explore1.png'),
  },
  {
    step: 2,
    title: 'Get Real Guidance',
    description:
      'Meet one-on-one to get clear, personalized styling in addition to curated closet support.',
    imageSource: require('@/assets/images/Profile2.png'),
  },
  {
    step: 3,
    title: 'Book Or Meet Instantly',
    description:
      'Schedule ahead for a virtual or in-person consult, or hop on an instant virtual call for immediate guidance.',
    imageSource: require('@/assets/images/Book3.png'),
  },
];

const HowEmmeWorksSection = () => {
  const { isWide: isWideSteps } = useWelcomeSectionLayout();

  return (
    <Section>
      <SectionInner isWideSteps={isWideSteps}>
        <SectionTitle>how emme works</SectionTitle>
        <StepsRow isWideSteps={isWideSteps}>
          {STEPS.map((item) => (
            <StepColumn key={item.step} isWideSteps={isWideSteps}>
              <StepBadge>
                <StepBadgeText>{item.step}</StepBadgeText>
              </StepBadge>
              <StepVisual
                accessible
                accessibilityRole="image"
                accessibilityLabel={`Step ${item.step}: ${item.title}`}
              >
                <StepVisualBackdrop />
                <StepScreenClip>
                  <StepScreenImage
                    source={item.imageSource}
                    resizeMode="contain"
                  />
                </StepScreenClip>
                <StepDeviceFrame
                  source={require('@/assets/images/iPhone 13 Pro.png')}
                  resizeMode="contain"
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants"
                />
              </StepVisual>
              <StepTitle isWideSteps={isWideSteps}>{item.title}</StepTitle>
              <StepDescription>{item.description}</StepDescription>
            </StepColumn>
          ))}
        </StepsRow>
      </SectionInner>
    </Section>
  );
};

const Section = styled.View({
  width: '100%',
  marginBottom: 24,
  paddingTop: WELCOME_HOW_EMME_WORKS_SECTION_PADDING_Y,
  paddingBottom: WELCOME_HOW_EMME_WORKS_SECTION_PADDING_Y,
});

const SectionInner = styled.View<StepsLayoutProps>(({ isWideSteps }) => ({
  width: '100%',
  maxWidth: isWideSteps ? HOW_EMME_WORKS_WIDE_INNER_MAX_WIDTH : undefined,
  alignSelf: 'center',
  paddingHorizontal: isWideSteps
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
}));

const SectionTitle = styled(H2)({
  color: sand,
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 40,
  letterSpacing: 1,
});

const StepsRow = styled.View<StepsLayoutProps>(({ isWideSteps }) => ({
  flexDirection: isWideSteps ? 'row' : 'column',
  flexWrap: isWideSteps ? 'nowrap' : undefined,
  justifyContent: 'center',
  alignItems: isWideSteps ? 'flex-start' : 'center',
  gap: isWideSteps ? 80 : 56,
  width: '100%',
}));

const StepColumn = styled.View<StepsLayoutProps>(({ isWideSteps }) => ({
  flex: isWideSteps ? 1 : undefined,
  flexShrink: isWideSteps ? 1 : undefined,
  minWidth: isWideSteps ? 0 : undefined,
  width: isWideSteps ? undefined : '100%',
  maxWidth: isWideSteps ? 400 : STEP_COLUMN_MAX_WIDTH_MOBILE,
  alignItems: 'center',
}));

const StepVisual = styled.View({
  width: '90%',
  maxWidth: 680,
  aspectRatio: STEP_DEVICE_FRAME_ASPECT,
  marginTop: 12,
  marginBottom: 20,
  alignSelf: 'center',
  position: 'relative',
  overflow: 'hidden',
  ...(IS_WEB ? { isolation: 'isolate' as const } : {}),
});

const StepVisualBackdrop = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: green,
  zIndex: 0,
});

const StepScreenClip = styled.View({
  position: 'absolute',
  ...STEP_SCREEN_INSET,
  overflow: 'hidden',
  borderRadius: 36,
  backgroundColor: sand,
  zIndex: 1,
});

const StepScreenImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const StepDeviceFrame = styled.Image({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
  zIndex: 2,
});

const StepBadge = styled.View({
  width: 32,
  height: 32,
  borderRadius: 16,
  backgroundColor: accent,
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 12,
});

const StepBadgeText = styled(BodyText)({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: green,
  fontWeight: '700',
  fontSize: 16,
  lineHeight: 20,
  marginBottom: 0,
});

const StepTitle = styled(BodyText)<StepsLayoutProps>(({ isWideSteps }) => ({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: sand,
  fontWeight: '400',
  textAlign: 'center',
  marginBottom: 12,
  fontSize: isWideSteps ? 18 : 17,
}));

const StepDescription = styled(BodyText)({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.welcomeHelveticaNeue,
  textAlign: 'center',
  fontWeight: '300',
  fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
  lineHeight: 24,
  letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
  marginBottom: 0,
});

export default HowEmmeWorksSection;
